const REGEX_PREFIX = '/(?i)^';
const REGEX_SUFFIX = '$/';
const DOT = '[.]';
/**
 * What `toSubdomainRegex` produces: hostname labels (plus escaped metachars) separated by `[.]`.
 * The wrapping `/…/` is optional: the first V2 values were stored without it.
 */
const SUBDOMAIN_REGEX = /^(\/?)\(\?i\)\^([a-z0-9-]+(?:\[\.\][a-z0-9-]+)*)\$\1$/;
/**
 * V1 (`wordFiltering.controller.js` `stringToRegExp`) format: every regex metachar of the value escaped as `[x]`,
 * wrapped as `/(?i)(…)/`, with an optional `.*` before (contains / ends with) and/or after (contains / starts with).
 */
const V1_REGEX = /^\/(\.\*)?\(\?i\)\(((?:\[[.*+?^${}()|[\]\\]\]|[^.*+?^${}()|[\]\\])+)\)(\.\*)?\/$/;
const V1_ESCAPED_CHAR = /\[([.*+?^${}()|[\]\\])\]/g;

const escapeLabel = (label: string): string => label.replace(/[\\^$*+?()[\]{}|]/g, '\\$&');

/** Case-insensitive anchored regex of a hostname, e.g. `a.b.com` -> `/(?i)^a[.]b[.]com$/`. */
export const toSubdomainRegex = (hostname: string): string =>
  `${REGEX_PREFIX}${hostname.toLowerCase().split('.').map(escapeLabel).join(DOT)}${REGEX_SUFFIX}`;

const fromV2SubdomainRegex = (value: string): string | undefined =>
  SUBDOMAIN_REGEX.exec(value)?.[2].split(DOT).join('.');

/** Plain text of a V1 regex (exact, contains, starts with or ends with) and whether it is the exact format. */
const parseV1Regex = (value: string): { isExact: boolean; text: string } | undefined => {
  const match = V1_REGEX.exec(value);
  return match ? { isExact: !match[1] && !match[3], text: match[2].replace(V1_ESCAPED_CHAR, '$1') } : undefined;
};

/**
 * Plain text of a stored subdomain regex, e.g. `woman.elperiodico.com` for any of:
 * - V2 (`toSubdomainRegex`): `/(?i)^woman[.]elperiodico[.]com$/` (or without the `/…/`)
 * - V1 exact: `/(?i)(woman[.]elperiodico[.]com)/`
 * - V1 ends with: `/.*(?i)(woman[.]elperiodico[.]com)/`
 * - V1 contains / starts with: the same as ends with / exact plus `.*` right before the closing `/`
 *
 * `undefined` for any other value (other patterns, plain hosts), which is shown as-is.
 */
export const fromSubdomainRegex = (value: string): string | undefined =>
  fromV2SubdomainRegex(value) ?? parseV1Regex(value)?.text;

/** Like `fromSubdomainRegex`, but only for the exact-match formats (V2 and V1 exact). */
export const fromExactSubdomainRegex = (value: string): string | undefined => {
  const v1 = parseV1Regex(value);
  return fromV2SubdomainRegex(value) ?? (v1?.isExact ? v1.text : undefined);
};
